/**
 * MIKOU - AJAX Add to Cart
 * Intercepts add-to-cart form submissions, adds via AJAX,
 * shows a confirmation notification, and updates cart count.
 * No redirect. No jQuery.
 */

(function () {
  'use strict';

  // ─── Configuration ────────────────────────────────────────────────────────
  const NOTIFICATION_DURATION = 4000; // ms before notification auto-hides
  // Detect locale prefix from current URL (e.g. /de/, /fr/)
  var localeMatch = window.location.pathname.match(/^\/([a-z]{2})\//);
  var localePrefix = localeMatch ? '/' + localeMatch[1] : '';
  var CART_URL = localePrefix + '/cart';

  // ─── Inject notification styles ───────────────────────────────────────────
  const style = document.createElement('style');
  style.textContent = `
    #mikou-cart-notification {
      position: fixed;
      bottom: -120px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      background: #ffffff;
      border: 1.5px solid #72642b;
      border-radius: 4px;
      padding: 14px 20px;
      min-width: 280px;
      max-width: 90vw;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.10);
      transition: bottom 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
      font-family: 'Lato', sans-serif;
    }

    #mikou-cart-notification.is-visible {
      bottom: 24px;
    }

    /* On mobile, sit above the sticky cart bar */
    @media screen and (max-width: 830px) {
      #mikou-cart-notification.is-visible {
        bottom: 80px;
      }
    }

    #mikou-cart-notification .notif__left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    #mikou-cart-notification .notif__check {
      width: 20px;
      height: 20px;
      background: #72642b;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    #mikou-cart-notification .notif__check svg {
      width: 11px;
      height: 11px;
      fill: none;
      stroke: #ffffff;
      stroke-width: 2.5;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    #mikou-cart-notification .notif__text {
      font-size: 13px;
      color: #222;
      letter-spacing: 0.01em;
    }

    #mikou-cart-notification .notif__actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    #mikou-cart-notification .notif__view-cart {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #72642b;
      text-decoration: none;
      white-space: nowrap;
      border-bottom: 1.5px solid #72642b;
      padding-bottom: 1px;
      transition: opacity 0.15s;
    }

    #mikou-cart-notification .notif__view-cart:hover {
      opacity: 0.7;
    }

    #mikou-cart-notification .notif__close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      color: #999;
      line-height: 1;
      font-size: 18px;
      display: flex;
      align-items: center;
    }

    #mikou-cart-notification .notif__close:hover {
      color: #444;
    }
  `;
  document.head.appendChild(style);

  // ─── Build notification DOM ────────────────────────────────────────────────
  const notification = document.createElement('div');
  notification.id = 'mikou-cart-notification';
  notification.setAttribute('role', 'status');
  notification.setAttribute('aria-live', 'polite');
  notification.innerHTML = `
    <div class="notif__left">
      <div class="notif__check">
        <svg viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3"/></svg>
      </div>
      <div class="notif__text">
        Added to cart
      </div>
    </div>
    <div class="notif__actions">
      <a href="${CART_URL}" class="notif__view-cart">View Cart</a>
      <button class="notif__close" aria-label="Close">&times;</button>
    </div>
  `;
  document.body.appendChild(notification);

  let hideTimer = null;

  function showNotification() {
    notification.classList.add('is-visible');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideNotification, NOTIFICATION_DURATION);
  }

  function hideNotification() {
    notification.classList.remove('is-visible');
  }

  notification.querySelector('.notif__close').addEventListener('click', hideNotification);

  // ─── Update cart count in header ──────────────────────────────────────────
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        // Common selectors used across Mono/Shopify themes
        var selectors = [
          '#CartCount',
          '.cart-count',
          '.cart__count',
          '[data-cart-count]',
          '.Header__CartCount',
          '.icon-cart__count'
        ];
        selectors.forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (el) {
            el.textContent = count;
            if (count > 0) el.classList.remove('hidden');
          });
        });
      })
      .catch(function () {});
  }

  // ─── Set button loading state ──────────────────────────────────────────────
  function setButtonLoading(button, loading) {
    if (!button) return;
    var spinner = button.querySelector('.loading-overlay__spinner');
    var text = button.querySelector('[data-add-to-cart-text]');
    if (loading) {
      button.setAttribute('disabled', 'disabled');
      if (spinner) spinner.classList.remove('hidden');
      if (text) text.style.opacity = '0';
    } else {
      button.removeAttribute('disabled');
      if (spinner) spinner.classList.add('hidden');
      if (text) text.style.opacity = '1';
    }
  }

  // ─── Handle form submission ────────────────────────────────────────────────
  function handleAddToCart(e) {
    var form = e.target.closest('form[data-product-form], form[action="/cart/add"], form[action*="/cart/add"]');
    if (!form) return;

    // Only intercept if this is an add-to-cart submit (not e.g. a search form)
    var submitBtn = e.target.closest('[data-add-to-cart]');
    if (!submitBtn) return;

    e.preventDefault();

    var variantInput = form.querySelector('[name="id"]');
    var quantityInput = form.querySelector('[name="quantity"]');
    if (!variantInput) return;

    var variantId = variantInput.value;
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    // Get product name for notification
    setButtonLoading(submitBtn, true);

    // Build locale-aware cart URL (handles /de/cart/add, /fr/cart/add, etc.)
    var cartAddUrl = (form.getAttribute('action') || '/cart/add').replace('/cart/add', '/cart/add.js');

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) { throw err; });
        }
        return response.json();
      })
      .then(function () {
        setButtonLoading(submitBtn, false);
        showNotification();
        updateCartCount();
      })
      .catch(function (error) {
        setButtonLoading(submitBtn, false);
        // Fall back to standard form submit so the user isn't stuck
        var errorMsg = (error && error.description) ? error.description : 'Could not add to cart. Please try again.';
        var errorWrapper = form.querySelector('.product-form__error-message-wrapper');
        var errorText = form.querySelector('.product-form__error-message');
        if (errorWrapper && errorText) {
          errorText.textContent = errorMsg;
          errorWrapper.removeAttribute('hidden');
        } else {
          alert(errorMsg);
        }
      });
  }

  // ─── Attach listeners ────────────────────────────────────────────────────
  // Use event delegation so it works for both the main form and the sticky form
  document.addEventListener('click', handleAddToCart);

})();
